import { useEffect, useState } from "react";
import { yearMonthBadge } from "@/lib/periodBadge";
import { Link } from "wouter";
import { useTimerContext, notesRequiredFor } from "@/lib/timerContext";
import { formatMinutesShort } from "@/lib/timerHold";
import { fetchWithAuth } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { SearchableSelect } from "@/components/custom/SearchableSelect";
import { DurationInput } from "@/components/custom/DurationInput";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Play,
  Square,
  Pause,
  RotateCcw,
  Plus,
  Check,
  Loader2,
  Clock,
  FolderPlus,
  PenLine,
  ChevronDown,
  ChevronUp,
  CalendarIcon,
  PauseCircle,
} from "lucide-react";
import { format, parseISO, addDays } from "date-fns";

export function TimerSheet() {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const {
    isSheetOpen, closeSheet,
    timerRunning, timerPaused, elapsedSeconds,
    handleStartTimer, handlePauseTimer, handleResumeTimer, handleStopTimer, handleResetTimer, formatTimer,
    timerStopped, heldTimer, canHold, handleHoldAndStartNew, handleDiscardStopped,
    entryDate, setEntryDate, editingId,
    selectedAccount, handleAccountChange,
    selectedProject, setSelectedProject,
    selectedCode, handleCodeChange,
    duration, setDuration,
    startTime, setStartTime,
    endTime, setEndTime,
    notes, setNotes,
    isBillable, setIsBillable,
    accounts, activityCodes, projects, includeClosedProjects, setIncludeClosedProjects,
    showAddProjectModal, setShowAddProjectModal,
    newProjectTitle, setNewProjectTitle,
    newProjectDescription, setNewProjectDescription,
    handleQuickAddProject, savingProject,
    handleSubmit, handleCancelEdit, saving,
  } = useTimerContext();

  // The two-step Discard resets whenever the stopped banner goes away.
  useEffect(() => { if (!timerStopped) setConfirmDiscard(false); }, [timerStopped]);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ── Quick-add entry form (independent of timer state) ──────────────────────
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickAccount, setQuickAccount] = useState("");
  const [quickCode, setQuickCode] = useState("");
  const [quickDate, setQuickDate] = useState(entryDate);
  const [quickDuration, setQuickDuration] = useState(0);
  const [quickStartTime, setQuickStartTime] = useState("");
  const [quickEndTime, setQuickEndTime] = useState("");
  const [quickNotes, setQuickNotes] = useState("");
  const [quickBillable, setQuickBillable] = useState(true);
  const [quickSaving, setQuickSaving] = useState(false);

  // Auto-compute duration from start+end time
  const computedQuickDuration = (() => {
    if (!quickStartTime || !quickEndTime) return null;
    const [sh, sm] = quickStartTime.split(":").map(Number);
    const [eh, em] = quickEndTime.split(":").map(Number);
    const startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    if (endMins <= startMins) endMins += 1440; // midnight crossing
    return (endMins - startMins) / 60;
  })();

  const effectiveDuration = computedQuickDuration !== null ? computedQuickDuration : quickDuration;

  const quickNotesRequired = notesRequiredFor(quickCode, activityCodes);
  const canQuickSave = !!quickAccount && quickAccount !== "none" && !!quickCode
    && effectiveDuration > 0 && !(quickNotesRequired && !quickNotes.trim());

  const handleQuickSave = async () => {
    if (!canQuickSave) return;
    setQuickSaving(true);
    try {
      await fetchWithAuth("/api/time-entries", {
        method: "POST",
        body: JSON.stringify({
          account_id: quickAccount,
          activity_code: quickCode,
          minutes: Math.round(effectiveDuration * 60),
          start_time: quickStartTime || null,
          end_time: quickEndTime || null,
          notes: quickNotes || null,
          billable: quickBillable,
          entry_date: quickDate,
        }),
      });
      toast({ title: "Entry saved" });
      queryClient.invalidateQueries({ queryKey: ["time-entries"] });
      // Reset quick-add form
      setQuickAccount("");
      setQuickCode("");
      setQuickDate(entryDate);
      setQuickDuration(0);
      setQuickStartTime("");
      setQuickEndTime("");
      setQuickNotes("");
      setQuickBillable(true);
      setQuickOpen(false);
    } catch (e: any) {
      const msg = e.message || "Unknown error";
      const description = msg.includes("closed pay period")
        ? "That date's pay period is closed. Select a more recent date."
        : msg.includes("approved")
        ? "That entry has been approved and cannot be changed."
        : msg;
      toast({ title: "Could not save entry", description, variant: "destructive" });
    } finally {
      setQuickSaving(false);
    }
  };

  const isToday = entryDate === format(new Date(), "yyyy-MM-dd");
  const isNotesRequired = notesRequiredFor(selectedCode, activityCodes);
  const canSave = !!selectedAccount && selectedAccount !== "none" && !!selectedCode
    && !(isNotesRequired && !notes.trim());

  return (
    <>
      <Sheet open={isSheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); }} modal={false}>
        <SheetContent side="right" className="w-[360px] sm:w-[400px] flex flex-col gap-0 p-0 overflow-hidden">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/50 shrink-0">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4 text-primary" />
              {editingId ? "Edit Entry" : isToday ? "Log Time — Today" : `Log Time — ${entryDate}`}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* ── Timer Controls (new entries only, today only) ── */}
            {!editingId && isToday && (
              <div className="space-y-2">
                {heldTimer && (
                  <div
                    className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                    data-testid="timer-on-hold"
                  >
                    <PauseCircle className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">
                      On hold:{" "}
                      <span className="font-medium">
                        {accounts.find((a) => a.id === heldTimer.selectedAccount)?.account_name || "No client yet"}
                      </span>
                      {heldTimer.selectedCode ? ` · ${heldTimer.selectedCode}` : ""}
                    </span>
                    <span className="ml-auto font-mono tabular-nums">{formatTimer(heldTimer.accumulatedSeconds)}</span>
                  </div>
                )}
                {timerStopped ? (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40"
                    data-testid="timer-stopped-banner"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                      <Square className="w-3.5 h-3.5 fill-current" />
                      Stopped: {formatMinutesShort(duration * 60)}, ready to save
                    </div>
                    <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-300/80">
                      Check the details below, then save.
                      {heldTimer ? " The timer on hold comes back after this one is saved or discarded." : ""}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button type="submit" form="timer-sheet-form" size="sm" className="h-8" disabled={saving}>
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Save entry
                      </Button>
                      {confirmDiscard ? (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="h-8"
                          onClick={() => { setConfirmDiscard(false); handleDiscardStopped(); }}
                        >
                          Discard {formatMinutesShort(duration * 60)}?
                        </Button>
                      ) : (
                        <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setConfirmDiscard(true)}>
                          Discard
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                <div className="bg-muted/30 rounded-lg p-3 border border-border/40">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {(timerRunning || timerPaused) ? (
                        <span
                          className={`font-mono text-xl font-bold tabular-nums ${
                            timerRunning ? "text-primary" : "text-muted-foreground"
                          }`}
                        >
                          {formatTimer(elapsedSeconds)}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Start timer or enter manually below</span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5">
                      {timerRunning ? (
                        <>
                          <Button type="button" variant="outline" size="sm" onClick={handlePauseTimer} className="h-8 w-8 p-0">
                            <Pause className="w-3.5 h-3.5" />
                          </Button>
                          <Button type="button" variant="default" size="sm" onClick={handleStopTimer} className="h-8 w-8 p-0">
                            <Square className="w-3.5 h-3.5 fill-current" />
                          </Button>
                        </>
                      ) : timerPaused ? (
                        <>
                          <Button type="button" variant="outline" size="sm" onClick={handleResumeTimer} className="h-8 w-8 p-0 text-primary">
                            <Play className="w-3.5 h-3.5 fill-current" />
                          </Button>
                          <Button type="button" variant="default" size="sm" onClick={handleStopTimer} className="h-8 w-8 p-0">
                            <Square className="w-3.5 h-3.5 fill-current" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={handleResetTimer} className="h-8 w-8 p-0 text-muted-foreground">
                            <RotateCcw className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      ) : (
                        <Button type="button" variant="outline" size="sm" onClick={handleStartTimer} className="h-8 px-3 text-primary border-primary gap-1.5">
                          <Play className="w-3 h-3 fill-current" />
                          Start Timer
                        </Button>
                      )}
                    </div>
                  </div>
                  {canHold && (
                    <button
                      type="button"
                      onClick={handleHoldAndStartNew}
                      className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      title="Put this timer on hold and start a new one, e.g. for a phone call"
                      data-testid="button-hold-start-new"
                    >
                      <PauseCircle className="w-3.5 h-3.5" />
                      Hold &amp; start new
                    </button>
                  )}
                </div>
                )}
              </div>
            )}

            {/* ── Entry Form ── */}
            <form id="timer-sheet-form" onSubmit={handleSubmit} className="space-y-3">
              {/* Date */}
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                {editingId ? (
                  <div className="h-9 flex items-center gap-2 px-3 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {format(parseISO(entryDate), "EEEE, MMM d, yyyy")}
                  </div>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full h-9 justify-start text-sm font-normal gap-2">
                        <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground" />
                        {entryDate === format(new Date(), "yyyy-MM-dd")
                          ? `Today — ${format(new Date(), "MMM d, yyyy")}`
                          : format(parseISO(entryDate), "EEEE, MMM d, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={parseISO(entryDate)}
                        onSelect={(date) => { if (date) setEntryDate(format(date, "yyyy-MM-dd")); }}
                        startMonth={new Date(new Date().getFullYear(), 0, 1)}
                        endMonth={addDays(new Date(), 14)}
                        disabled={(date) => date > addDays(new Date(), 14) || date < new Date(new Date().getFullYear(), 0, 1)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>

              {/* Account */}
              <div className="space-y-1">
                <Label className="text-xs">Client / Account <span className="text-destructive">*</span></Label>
                <SearchableSelect
                  value={selectedAccount}
                  onValueChange={handleAccountChange}
                  options={accounts.map((a) => ({ value: a.id, label: a.account_name }))}
                  placeholder="Search accounts..."
                  searchPlaceholder="Type to search..."
                  emptyText="No accounts found."
                  className="w-full"
                />
              </div>

              {/* Project */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Project</Label>
                  <label className="flex items-center gap-1 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-3 w-3 rounded"
                      checked={includeClosedProjects}
                      onChange={(e) => setIncludeClosedProjects(e.target.checked)}
                    />
                    <span className="text-[10px] text-muted-foreground">Include closed</span>
                  </label>
                </div>
                <div className="flex gap-2">
                  <SearchableSelect
                    value={selectedProject}
                    onValueChange={setSelectedProject}
                    options={[
                      { value: "none", label: "No project" },
                      ...projects.map((p) => {
                        const period = p.computed_accounting_period ?? (p.tax_year ? yearMonthBadge(p.tax_year, p.period_label) : null);
                        return { value: p.id, label: period ? `${p.title} — ${period}` : p.title };
                      }),
                    ]}
                    placeholder="Search projects..."
                    searchPlaceholder="Type to search..."
                    emptyText="No projects found."
                    disabled={!selectedAccount || selectedAccount === "none"}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setShowAddProjectModal(true)}
                    disabled={!selectedAccount || selectedAccount === "none"}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Activity Code */}
              <div className="space-y-1">
                <Label className="text-xs">Activity Code <span className="text-destructive">*</span></Label>
                <SearchableSelect
                  value={selectedCode}
                  onValueChange={handleCodeChange}
                  options={activityCodes.map((c) => ({ value: c.code, label: c.name, sublabel: c.code }))}
                  placeholder="Search activities..."
                  searchPlaceholder="Type to search..."
                  emptyText="No activities found."
                  className="w-full"
                />
              </div>

              {/* Time + Duration + Billable */}
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Start Time</Label>
                    <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="h-9 w-full min-w-0" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">End Time</Label>
                    <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="h-9 w-full min-w-0" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Duration</Label>
                    <DurationInput value={duration} onChange={setDuration} compact />
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="space-y-1 shrink-0">
                    <Label className="text-xs">Billable</Label>
                    <div className="h-9 flex items-center">
                      <Switch checked={isBillable} onCheckedChange={setIsBillable} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-1">
                <Label className="text-xs">
                  Notes{" "}
                  {isNotesRequired
                    ? <span className="text-destructive">*</span>
                    : <span className="text-muted-foreground">(optional)</span>}
                </Label>
                <Textarea
                  placeholder={isNotesRequired ? "Notes required for this activity code..." : "Notes..."}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={`resize-none min-h-[72px] text-sm ${isNotesRequired && !notes.trim() ? "border-destructive/50" : ""}`}
                />
              </div>
            </form>
          </div>

          {/* ── Quick Add Entry (while timer is running) ── */}
          {(timerRunning || timerPaused) && !editingId && (
            <div className="border-t border-dashed border-border/50 shrink-0">
              <button
                type="button"
                onClick={() => setQuickOpen((o) => !o)}
                className="w-full flex items-center justify-between px-5 py-2.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="flex items-center gap-1.5">
                  <PenLine className="w-3.5 h-3.5" />
                  Add a different entry while timer runs
                </span>
                {quickOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>

              {quickOpen && (
                <div className="px-5 pb-4 space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 justify-start text-xs font-normal gap-2">
                          <CalendarIcon className="w-3 h-3 text-muted-foreground" />
                          {quickDate === format(new Date(), "yyyy-MM-dd")
                            ? `Today — ${format(new Date(), "MMM d, yyyy")}`
                            : format(parseISO(quickDate), "EEE, MMM d, yyyy")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={parseISO(quickDate)}
                          onSelect={(date) => { if (date) setQuickDate(format(date, "yyyy-MM-dd")); }}
                          startMonth={new Date(new Date().getFullYear(), 0, 1)}
                          endMonth={addDays(new Date(), 14)}
                          disabled={(date) => date > addDays(new Date(), 14) || date < new Date(new Date().getFullYear(), 0, 1)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Client <span className="text-destructive">*</span></Label>
                    <SearchableSelect
                      value={quickAccount}
                      onValueChange={setQuickAccount}
                      options={accounts.map((a) => ({ value: a.id, label: a.account_name }))}
                      placeholder="Search accounts..."
                      searchPlaceholder="Type to search..."
                      emptyText="No accounts found."
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Activity Code <span className="text-destructive">*</span></Label>
                    <SearchableSelect
                      value={quickCode}
                      onValueChange={setQuickCode}
                      options={activityCodes.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }))}
                      placeholder="Select code..."
                      searchPlaceholder="Search codes..."
                      emptyText="No codes found."
                      className="w-full"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Start</Label>
                      <Input type="time" value={quickStartTime} onChange={(e) => setQuickStartTime(e.target.value)} className="h-9 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">End</Label>
                      <Input type="time" value={quickEndTime} onChange={(e) => setQuickEndTime(e.target.value)} className="h-9 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">
                        Duration {!quickStartTime && !quickEndTime && <span className="text-destructive">*</span>}
                      </Label>
                      <DurationInput
                        value={computedQuickDuration !== null ? computedQuickDuration : quickDuration}
                        onChange={computedQuickDuration !== null ? () => {} : setQuickDuration}
                        compact
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Enter start+end or just duration</span>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Billable</Label>
                      <Switch checked={quickBillable} onCheckedChange={setQuickBillable} />
                    </div>
                  </div>
                  {quickNotesRequired && (
                    <div className="space-y-1">
                      <Label className="text-xs">Notes <span className="text-destructive">*</span></Label>
                      <Textarea
                        value={quickNotes}
                        onChange={(e) => setQuickNotes(e.target.value)}
                        placeholder="Required for this activity code…"
                        className="resize-none min-h-[60px] text-sm"
                      />
                    </div>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    className="w-full"
                    disabled={!canQuickSave || quickSaving}
                    onClick={handleQuickSave}
                  >
                    {quickSaving
                      ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      : <Plus className="mr-2 h-3.5 w-3.5" />}
                    Save Entry (timer keeps running)
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ── Footer ── */}
          <div className="px-5 pb-5 pt-3 border-t border-border/50 shrink-0 space-y-2">
            <div className="text-center">
              <Link href="/today" className="text-xs text-muted-foreground hover:text-primary hover:underline transition-colors">
                ↗ Open full Time page
              </Link>
            </div>
            <div className="flex gap-2">
            {editingId && (
              <Button type="button" variant="outline" className="flex-1" onClick={handleCancelEdit}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              form="timer-sheet-form"
              className="flex-1"
              disabled={!canSave || saving}
            >
              {saving
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : editingId
                  ? <><Check className="mr-2 h-4 w-4" /> Save Changes</>
                  : <><Plus className="mr-2 h-4 w-4" /> Add Entry</>}
            </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Quick-add project modal */}
      <Dialog open={showAddProjectModal} onOpenChange={setShowAddProjectModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Project</DialogTitle>
            <DialogDescription>
              Create a new project for {accounts.find((a) => a.id === selectedAccount)?.account_name || "this account"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sheet-project-title">Project Title <span className="text-destructive">*</span></Label>
              <Input
                id="sheet-project-title"
                value={newProjectTitle}
                onChange={(e) => setNewProjectTitle(e.target.value)}
                placeholder="Enter project title..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sheet-project-desc">Description (optional)</Label>
              <Textarea
                id="sheet-project-desc"
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                placeholder="Brief description..."
                className="resize-none"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProjectModal(false)}>Cancel</Button>
            <Button onClick={handleQuickAddProject} disabled={!newProjectTitle.trim() || savingProject}>
              {savingProject ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FolderPlus className="mr-2 h-4 w-4" />}
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
